import type { Id } from "./convex";

/**
 * Calls Gemini via the backend proxy (keeps API key server-side).
 * Falls back to the VITE_PDF_BACKEND_URL used by the dashboard.
 */
// Strip any trailing slash so `${BACKEND_URL}/gemini` never produces `//gemini`.
const BACKEND_URL = (
  import.meta.env.VITE_PDF_BACKEND_URL || "http://localhost:8080"
).replace(/\/+$/, "");

// --- Comptabilité des appels d'IA -----------------------------------------
//
// Mesurée ICI, dans le client, et non chez les appelants. `passport-ocr.ts` et
// `document-ocr.ts` importaient bien `logOcrTransaction`... sans jamais
// l'appeler : l'OCR des passeports, le plus gros volume d'IA du produit, ne
// comptait rien. Un compteur posé chez l'appelant est un compteur qu'on oublie
// de brancher ; posé ici, il ne peut pas être contourné.
let currentFirmId: string | null = null;
let currentSubmissionId: string | null = null;

/** Rattache les appels suivants au dossier en cours de remplissage. */
export function setAiUsageContext(ctx: {
  firmId?: string | null;
  submissionId?: string | null;
}): void {
  if (ctx.firmId !== undefined) currentFirmId = ctx.firmId;
  if (ctx.submissionId !== undefined) currentSubmissionId = ctx.submissionId;
}

type Usage = {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  totalTokenCount?: number;
};

function logUsage(context: string, model: string | undefined, usage?: Usage) {
  if (!usage?.totalTokenCount) return;
  // Import paresseux : `convex.ts` tire tout le client Convex, et ce module est
  // chargé à la demande par la traduction des réponses.
  import("./convex")
    .then(({ convexClient, api }) =>
      convexClient.mutation(api.aiUsageLogs.logAiUsage, {
        ...(currentFirmId ? { firmId: currentFirmId as Id<"firms"> } : {}),
        ...(currentSubmissionId
          ? { submissionId: currentSubmissionId as Id<"submissions"> }
          : {}),
        modelName: model ?? "gemini-2.5-flash",
        promptTokens: usage.promptTokenCount ?? 0,
        completionTokens: usage.candidatesTokenCount ?? 0,
        totalTokens: usage.totalTokenCount!,
        context,
      }),
    )
    // La mesure ne doit jamais faire échouer ce qu'elle mesure.
    .catch(() => {});
}


// --- Laissez-passer -------------------------------------------------------
//
// Le proxy détient la clé de facturation ; il exige désormais une signature
// courte que seul Formio peut produire. Gardée en mémoire et renouvelée avant
// l'échéance : la demander à chaque appel ajouterait un aller-retour à chaque
// page de document scannée.
let jeton: { token: string; expiresAt: number } | null = null;

async function laissezPasser(): Promise<string | null> {
  // Une minute de marge : un jeton qui expire pendant l'envoi serait refusé
  // à l'arrivée alors qu'il était valide au départ.
  if (jeton && jeton.expiresAt - Date.now() > 60_000) return jeton.token;
  try {
    const { convexClient, api } = await import("./convex");
    const r = await convexClient.action(api.proxyAuth.mintProxyToken, {
      submissionId: currentSubmissionId ?? undefined,
    });
    jeton = r ?? null;
    return jeton?.token ?? null;
  } catch {
    // Pas de jeton = appel non signé. Le proxy le tolère pendant la bascule ;
    // faire échouer la lecture du passeport serait pire.
    return null;
  }
}

async function entetes(): Promise<Record<string, string>> {
  const t = await laissezPasser();
  return {
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

// Google ne sert plus `gemini-2.0-flash` : l'appel remonte en 404, que le proxy
// relaie en 502. C'était le défaut de ce module, et son seul appelant est la
// traduction des réponses du client — muette en production depuis le retrait du
// modèle. Le défaut est nommé une fois : la requête et le compteur de coûts
// partagent la même constante, sinon on facture un modèle et on en appelle un
// autre.
const MODELE_PAR_DEFAUT = "gemini-2.5-flash";

export async function callGemini(
  prompt: string,
  model?: string,
  context = "form:gemini",
): Promise<string> {
  const modele = model || MODELE_PAR_DEFAUT;
  const response = await fetch(`${BACKEND_URL}/gemini`, {
    method: "POST",
    headers: await entetes(),
    body: JSON.stringify({
      model: modele,
      contents: [{ parts: [{ text: prompt }] }],
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Gemini proxy error (${response.status})`);
  }

  const { text, usageMetadata } = await response.json();
  logUsage(context, modele, usageMetadata);
  return text || "";
}

/**
 * Calls Gemini with image content via the backend proxy.
 */
export async function callGeminiWithImage(
  prompt: string,
  imageBase64: string,
  mimeType: string,
  model?: string,
  context = "form:ocr",
): Promise<string> {
  const response = await fetch(`${BACKEND_URL}/gemini`, {
    method: "POST",
    headers: await entetes(),
    body: JSON.stringify({
      model: model || "gemini-2.5-flash",
      contents: [
        {
          parts: [
            { text: prompt },
            {
              inlineData: {
                mimeType,
                data: imageBase64,
              },
            },
          ],
        },
      ],
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Gemini proxy error (${response.status})`);
  }

  const { text, usageMetadata } = await response.json();
  logUsage(context, model || "gemini-2.5-flash", usageMetadata);
  return text || "";
}
